import { Popover, Button, SegmentedControl, TextInput, Tooltip, Text, ActionIcon } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useState, useEffect } from 'react';
import { IconLoader, IconInfoCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import { api } from '~/trpc-extra/api';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';
import { zVariableValidationType } from '~/common/variable/validation';

export function ComponentCallOpEdit({
  componentId,
  testContentId,
  readonly,
  editing,
  onEditingChange,
  parentContext,
  children,
}: {
  componentId: string;
  testContentId: string;
  readonly?: boolean;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  parentContext?: OperationParentContext;
  children: React.ReactNode;
}) {
  const [activeTab, setActiveTab] = useState<string | null>('parameters');
  const trpcUtils = api.useUtils();

  // Fetch full component data when editing for parameters/outputs
  const { data: currentComponent, isLoading: isLoadingCurrentComponent } = api.component.get.useQuery(
    { id: componentId },
    { enabled: editing && !!componentId },
  );

  const componentEditForm = useForm({
    initialValues: {
      parameters: currentComponent?.parameters || [],
      outputVariables: currentComponent?.outputVariables || [],
    },
  });

  // Update form when component data loads
  useEffect(() => {
    if (currentComponent) {
      componentEditForm.setValues({
        parameters: currentComponent.parameters || [],
        outputVariables: currentComponent.outputVariables || [],
      });
    }
  }, [isLoadingCurrentComponent]);

  const { mutate: updateComponent, isPending: isUpdatingComponent } = api.component.update.useMutation({
    onSuccess: () => {
      notifications.show({
        title: 'Component updated',
        message: 'Component parameters and outputs updated successfully',
      });
      if (parentContext?.componentId) {
        void trpcUtils.component.get.invalidate({ id: parentContext.componentId });
      } else if (parentContext?.conditional) {
        void trpcUtils.operation.getFullConditionalBlock.invalidate({
          conditionalBlockId: parentContext.conditional.conditionalBlockId,
        });
      } else {
        void trpcUtils.testContent.get.invalidate({ id: testContentId });
      }
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red',
      });
    },
  });

  const updateComponentDefinition = () => {
    const { parameters, outputVariables } = componentEditForm.values;

    // Validate all parameter names
    const parameterValidationErrors: string[] = [];
    parameters.forEach((param, index) => {
      if (!param.trim()) {
        parameterValidationErrors.push(`Parameter ${index + 1}: Name is required`);
        return;
      }

      const result = zVariableValidationType.safeParse(param.trim());
      if (!result.success) {
        parameterValidationErrors.push(
          `Parameter "${param}": Invalid. Must start with a letter or underscore, followed by letters, numbers, or underscores`,
        );
      }
    });

    // Validate all output variable names
    const outputValidationErrors: string[] = [];
    outputVariables.forEach((variable, index) => {
      if (!variable.trim()) {
        outputValidationErrors.push(`Output variable ${index + 1}: Name is required`);
        return;
      }

      const result = zVariableValidationType.safeParse(variable.trim());
      if (!result.success) {
        outputValidationErrors.push(
          `Output variable "${variable}": Invalid. Must start with a letter or underscore, followed by letters, numbers, or underscores`,
        );
      }
    });

    // Check for duplicates within parameters
    const uniqueParameters = [...new Set(parameters.map((p) => p.trim()))];
    if (uniqueParameters.length !== parameters.length) {
      parameterValidationErrors.push('Duplicate parameter names are not allowed');
    }

    // Check for duplicates within output variables
    const uniqueOutputVariables = [...new Set(outputVariables.map((v) => v.trim()))];
    if (uniqueOutputVariables.length !== outputVariables.length) {
      outputValidationErrors.push('Duplicate output variable names are not allowed');
    }

    // Check for conflicts between parameters and output variables
    const parameterSet = new Set(parameters.map((p) => p.trim()));
    const conflictingOutputs = outputVariables.filter((v) => parameterSet.has(v.trim()));
    if (conflictingOutputs.length > 0) {
      outputValidationErrors.push(
        `Output variables cannot have the same name as parameters: ${conflictingOutputs.join(', ')}`,
      );
    }

    // If there are any validation errors, show them and return
    const allErrors = [...parameterValidationErrors, ...outputValidationErrors];
    if (allErrors.length > 0) {
      notifications.show({
        title: 'Validation Error',
        message: (
          <div className="flex flex-col gap-1.5">
            {allErrors.map((error, index) => (
              <div key={index}>• {error}</div>
            ))}
          </div>
        ),
        color: 'red',
        autoClose: 5000,
      });
      return;
    }

    // Trim whitespace from all names
    const trimmedParameters = parameters.map((p) => p.trim());
    const trimmedOutputVariables = outputVariables.map((v) => v.trim());

    onEditingChange(false);
    updateComponent({
      id: componentId,
      update: {
        parameters: trimmedParameters,
        outputVariables: trimmedOutputVariables,
      },
    });
  };

  return (
    <Popover
      width={400}
      position="bottom-start"
      withArrow
      shadow="md"
      opened={editing}
      onClose={() => onEditingChange(false)}
      onDismiss={() => onEditingChange(false)}
    >
      <Popover.Target>{children}</Popover.Target>

      <Popover.Dropdown>
        <div className="flex flex-col gap-3">
          <SegmentedControl
            value={activeTab || 'parameters'}
            onChange={setActiveTab}
            data={[
              { label: 'Parameters', value: 'parameters' },
              { label: 'Outputs', value: 'outputs' },
            ]}
            fullWidth
          />

          {activeTab === 'parameters' && (
            <div>
              {isLoadingCurrentComponent ?
                <div className="flex justify-center py-4">
                  <IconLoader
                    size={20}
                    className="animate-spin"
                  />
                </div>
              : <div className="flex flex-col gap-4">
                  <div>
                    <div className="mb-2 flex items-end justify-between">
                      <Tooltip
                        label="Parameters allow passing custom values when calling the component"
                        position="top"
                      >
                        <div className="flex items-center gap-1">
                          <Text
                            fw={500}
                            size="sm"
                          >
                            Input Parameters
                          </Text>
                          <IconInfoCircle size={14} />
                        </div>
                      </Tooltip>
                      <Button
                        size="compact-xs"
                        variant="default"
                        leftSection={<IconPlus size={14} />}
                        disabled={readonly}
                        onClick={() => {
                          componentEditForm.setFieldValue('parameters', componentEditForm.values.parameters.concat(''));
                        }}
                      >
                        Parameter
                      </Button>
                    </div>

                    {componentEditForm.values.parameters.length === 0 ?
                      <Text
                        size="sm"
                        c="dimmed"
                      >
                        No parameters defined
                      </Text>
                    : <div className="flex flex-col gap-2">
                        {componentEditForm.values.parameters.map((parameter, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2"
                          >
                            <TextInput
                              placeholder="Parameter name"
                              value={parameter}
                              disabled={readonly}
                              onChange={(event) => {
                                const newParameters = componentEditForm.values.parameters.slice();
                                newParameters[index] = event.currentTarget.value;
                                componentEditForm.setFieldValue('parameters', newParameters);
                              }}
                              className="flex-1"
                            />
                            <ActionIcon
                              variant="default"
                              disabled={readonly}
                              onClick={() => {
                                const newParameters = componentEditForm.values.parameters.filter((_, i) => i !== index);
                                componentEditForm.setFieldValue('parameters', newParameters);
                              }}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </div>
                        ))}
                      </div>
                    }
                  </div>

                  <div className="flex justify-end gap-1">
                    <Button
                      variant="light"
                      onClick={() => onEditingChange(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={updateComponentDefinition}
                      loading={isUpdatingComponent}
                      disabled={readonly}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              }
            </div>
          )}

          {activeTab === 'outputs' && (
            <div>
              {isLoadingCurrentComponent ?
                <div className="flex justify-center py-4">
                  <IconLoader
                    size={20}
                    className="animate-spin"
                  />
                </div>
              : <div className="flex flex-col gap-4">
                  <div>
                    <div className="mb-2 flex items-end justify-between">
                      <Tooltip
                        label="Output variables can be returned for use in subsequent operations"
                        position="top"
                      >
                        <div className="flex items-center gap-1">
                          <Text
                            fw={500}
                            size="sm"
                          >
                            Output Variables
                          </Text>
                          <IconInfoCircle size={14} />
                        </div>
                      </Tooltip>
                      <Button
                        size="compact-xs"
                        variant="default"
                        leftSection={<IconPlus size={14} />}
                        disabled={readonly}
                        onClick={() => {
                          componentEditForm.setFieldValue(
                            'outputVariables',
                            componentEditForm.values.outputVariables.concat(''),
                          );
                        }}
                      >
                        Output Variable
                      </Button>
                    </div>

                    {componentEditForm.values.outputVariables.length === 0 ?
                      <Text
                        size="sm"
                        c="dimmed"
                      >
                        No output variables defined
                      </Text>
                    : <div className="flex flex-col gap-2">
                        {componentEditForm.values.outputVariables.map((variable, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2"
                          >
                            <TextInput
                              placeholder="Variable name"
                              value={variable}
                              disabled={readonly}
                              onChange={(event) => {
                                const newOutputVariables = componentEditForm.values.outputVariables.slice();
                                newOutputVariables[index] = event.currentTarget.value;
                                componentEditForm.setFieldValue('outputVariables', newOutputVariables);
                              }}
                              className="flex-1"
                            />
                            <ActionIcon
                              variant="default"
                              disabled={readonly}
                              onClick={() => {
                                const newOutputVariables = componentEditForm.values.outputVariables.filter(
                                  (_, i) => i !== index,
                                );
                                componentEditForm.setFieldValue('outputVariables', newOutputVariables);
                              }}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </div>
                        ))}
                      </div>
                    }
                  </div>

                  <div className="flex justify-end gap-1">
                    <Button
                      variant="light"
                      onClick={() => onEditingChange(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={updateComponentDefinition}
                      loading={isUpdatingComponent}
                      disabled={readonly}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              }
            </div>
          )}
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
